/**
 * The figures the audience trend's summary can show, in display order: four
 * read from the series itself, three from the report (the piracy audience
 * indicator's metrics, fetched only when switched on).
 */
export const SUMMARY_FIGURES = [
  { setting: "summaryShowLatest", kind: "latest", default: true },
  { setting: "summaryShowPeak", kind: "peak", default: true },
  { setting: "summaryShowAverage", kind: "metric", metricId: "average-viewers", default: false },
  { setting: "summaryShowMax", kind: "metric", metricId: "max-viewers", default: false },
  {
    setting: "summaryShowDisrupted",
    kind: "metric",
    metricId: "viewers-disrupted",
    default: false,
  },
  { setting: "summaryShowSafe", kind: "safe", default: true },
  { setting: "summaryShowBlocking", kind: "blocking", default: true },
] as const;

export type SummaryFigure = (typeof SUMMARY_FIGURES)[number];
export type SummaryFigureSetting = SummaryFigure["setting"];

/** The figures switched on (a figure follows its default until set). */
export function shownSummaryFigures(
  config: Partial<Record<SummaryFigureSetting, boolean | undefined>>,
): SummaryFigure[] {
  return SUMMARY_FIGURES.filter((figure) => config[figure.setting] ?? figure.default);
}

/** The report metrics the shown figures need: exactly these are fetched. */
export function summaryMetricIds(figures: readonly SummaryFigure[]): string[] {
  return figures.flatMap((figure) => (figure.kind === "metric" ? [figure.metricId] : []));
}

/**
 * Balanced rows for `count` figures with at most `capacity` per row: as few
 * rows as possible, their sizes differing by one at most (7 in rows of 3 make
 * 3 + 2 + 2, never 3 + 3 + 1), each row filling the width. Returned as a grid:
 * `columns` shared by every row, and each figure's `span` of them.
 */
export function balancedRows(
  count: number,
  capacity: number,
): { columns: number; spans: number[] } {
  if (count <= 0) return { columns: 1, spans: [] };
  const rows = Math.ceil(count / Math.max(1, capacity));
  const small = Math.floor(count / rows);
  const large = Math.ceil(count / rows);
  const larger = count - small * rows; // rows taking one more figure, first
  const columns = small === large ? small : small * large;
  const spans: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    const size = row < larger ? large : small;
    for (let index = 0; index < size; index += 1) spans.push(columns / size);
  }
  return { columns, spans };
}
